import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Pencil, Trash2, UploadCloud, RefreshCw, Loader2 } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { inputCls, errMsg, CategorySelect } from "./shared";

const EMPTY = { title: "", description: "", category: "", year: "", file: "" };

export default function DocManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadPdf = async (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("केवल PDF फाइल चुनें");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/admin/upload?kind=document&context=reports${form.category ? `&category=${form.category}` : ""}`, fd, {
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      setForm((p) => ({ ...p, file: r.data.url, title: p.title || f.name.replace(/\.[^.]+$/, "") }));
      toast.success("PDF अपलोड हुई");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error("कृपया PDF अपलोड करें");
      return;
    }
    try {
      if (editing) {
        await api.put(`/admin/documents/${editing}`, form);
        toast.success("दस्तावेज़ अपडेट हुआ");
      } else {
        await api.post("/admin/documents", form);
        toast.success("दस्तावेज़ जोड़ा गया");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या आप यह दस्तावेज़ हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <form data-testid="doc-form" onSubmit={save} className="bg-white rounded-2xl border border-ngo-deep/10 p-6 mb-10">
        <h3 className="font-heading font-bold text-lg text-ngo-ink mb-5">{editing ? "दस्तावेज़ संपादित करें" : "नया दस्तावेज़ / PDF जोड़ें"}</h3>
        <div
          data-testid="doc-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadPdf(e.dataTransfer.files?.[0]); }}
          onClick={() => fileRef.current?.click()}
          className={`rounded-2xl border-2 border-dashed px-6 py-8 text-center cursor-pointer transition-colors duration-200 mb-5 ${dragOver ? "border-ngo-leaf bg-ngo-mint" : "border-ngo-leaf/40 hover:bg-ngo-mint/60"}`}
        >
          {uploading ? (
            <>
              <Loader2 size={30} className="mx-auto text-ngo-deep animate-spin" />
              <p className="mt-2 text-sm font-bold text-ngo-deep">अपलोड हो रहा है... {progress}%</p>
            </>
          ) : form.file ? (
            <>
              <FileText size={30} className="mx-auto text-ngo-deep" />
              <p className="mt-2 text-sm font-bold text-ngo-deep">PDF अपलोड हो गई</p>
              <p className="text-xs text-ngo-ink/50 break-all mt-1">{form.file}</p>
              <p className="text-xs text-ngo-leaf mt-2 font-semibold flex items-center justify-center gap-1"><RefreshCw size={12} /> बदलने के लिए फिर क्लिक करें</p>
            </>
          ) : (
            <>
              <UploadCloud size={30} className="mx-auto text-ngo-deep/60" />
              <p className="mt-2 text-sm font-semibold text-ngo-deep">PDF यहां खींचकर छोड़ें या क्लिक करके चुनें</p>
              <p className="mt-1 text-xs text-ngo-ink/50">केवल PDF • अधिकतम 20MB</p>
            </>
          )}
          <input ref={fileRef} data-testid="doc-file-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => { uploadPdf(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">दस्तावेज़ शीर्षक *</label>
            <input data-testid="doc-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">वर्ष/तारीख</label>
            <input data-testid="doc-year" className={inputCls} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2025-26" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">श्रेणी</label>
            <CategorySelect testid="doc-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} allowEmpty />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">संक्षिप्त विवरण</label>
            <input data-testid="doc-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="doc-save" className="px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
            {editing ? "अपडेट करें" : "दस्तावेज़ जोड़ें"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} className="px-6 py-3 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold">रद्द करें</button>
          )}
        </div>
      </form>

      {items.length === 0 && <p data-testid="doc-empty" className="text-center text-ngo-ink/50 py-14">अभी कोई दस्तावेज़ नहीं है।</p>}
      <div data-testid="doc-list" className="space-y-4">
        {items.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-5 flex flex-wrap items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-ngo-mint flex items-center justify-center text-ngo-deep shrink-0"><FileText size={21} /></div>
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-ngo-ink">{d.title}</p>
              <p className="text-xs text-ngo-ink/50 mt-0.5">{[d.year, d.category].filter(Boolean).join(" • ")}</p>
              {d.description && <p className="text-sm text-ngo-ink/60 mt-1">{d.description}</p>}
            </div>
            <div className="flex gap-1.5">
              <a data-testid={`doc-view-${d.id}`} href={resolveMedia(d.file)} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200" aria-label="देखें">
                <FileText size={15} />
              </a>
              <button data-testid={`doc-edit-${d.id}`} onClick={() => { setEditing(d.id); setForm({ title: d.title, description: d.description, category: d.category, year: d.year, file: d.file }); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="संपादित करें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200">
                <Pencil size={15} />
              </button>
              <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)} aria-label="हटाएं" className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors duration-200">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
